import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2, Search, Plus, Users, Star, Link as LinkIcon, Phone, Mail, X, Loader2, Globe
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { getMediaUrl } from '@/utils/mediaUtils'

interface Page {
  id: number
  name: string
  slug: string
  description?: string
  category?: string
  profileImage?: string
  coverImage?: string
  contactEmail?: string
  contactPhone?: string
  website?: string
  address?: string
  followerCount?: number
  isVerified?: boolean
  isAdmin?: boolean
  isFollowing?: boolean
  createdAt: string
}

const categories = [
  { value: 'HEALTH', label: 'Health & Wellness' },
  { value: 'NGO', label: 'NGO / Non-Profit' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'COMMUNITY', label: 'Community' },
  { value: 'OTHER', label: 'Other' }
]

const PagesPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [pages, setPages] = useState<Page[]>([])
  const [followedPages, setFollowedPages] = useState<Page[]>([])
  const [activeTab, setActiveTab] = useState<'following' | 'discover'>('discover')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedPage, setSelectedPage] = useState<Page | null>(null)
  const [category, setCategory] = useState('')
  const [creating, setCreating] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'HEALTH',
    contactEmail: '',
    contactPhone: '',
    website: ''
  })

  const fetchPages = useCallback(async () => {
    setLoading(true)
    try {
      const [popularRes, followingRes] = await Promise.all([
        api.get('/pages/popular?page=0&size=50'),
        isAuthenticated ? api.get('/pages/following?page=0&size=50') : Promise.resolve({ data: { success: true, data: { content: [] } } })
      ])

      if (popularRes.data.success) {
        setPages(popularRes.data.data?.content || [])
      }
      if (followingRes.data.success && isAuthenticated) {
        setFollowedPages(followingRes.data.data?.content || [])
      }
    } catch (error) {
      console.error('Error fetching pages:', error)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchPages()
  }, [fetchPages])

  const followPage = useCallback(async (pageId: number) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    try {
      const response = await api.post(`/pages/${pageId}/follow`)
      if (response.data.success) {
        fetchPages()
      }
    } catch (error) {
      console.error('Error following page:', error)
    }
  }, [isAuthenticated, navigate, fetchPages])

  const unfollowPage = useCallback(async (pageId: number) => {
    try {
      const response = await api.post(`/pages/${pageId}/unfollow`)
      if (response.data.success) {
        fetchPages()
      }
    } catch (error) {
      console.error('Error unfollowing page:', error)
    }
  }, [fetchPages])

  const isFollowingPage = useCallback((pageId: number) => {
    return followedPages.some(p => p.id === pageId)
  }, [followedPages])

  const handleFollowClick = useCallback((e: React.MouseEvent, pageId: number) => {
    e.stopPropagation()
    isFollowingPage(pageId) ? unfollowPage(pageId) : followPage(pageId)
  }, [isFollowingPage, unfollowPage, followPage])

  const createPage = useCallback(async () => {
    if (!formData.name.trim()) return
    setCreating(true)
    try {
      const response = await api.post('/pages', formData)
      if (response.data.success) {
        setShowCreateModal(false)
        setFormData({ name: '', description: '', category: 'HEALTH', contactEmail: '', contactPhone: '', website: '' })
        fetchPages()
      }
    } catch (error) {
      console.error('Error creating page:', error)
    } finally {
      setCreating(false)
    }
  }, [formData, fetchPages])

  const filteredPages = (activeTab === 'following' ? followedPages : pages).filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !category || p.category === category
    return matchesSearch && matchesCategory
  })

  const closeModal = useCallback(() => {
    setShowCreateModal(false)
  }, [])

  const closePageModal = useCallback(() => {
    setSelectedPage(null)
  }, [])

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true)
  }, [])

  return (
    <GovLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <GovPageTitle
          title="Pages"
          subtitle="Follow organizations and causes in TB awareness"
          icon={Building2}
        />
        {isAuthenticated && (
          <GovButton onClick={openCreateModal} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create Page
          </GovButton>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('following')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'following' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Star className="w-4 h-4 inline mr-1.5" /> Following
        </button>
        <button
          onClick={() => setActiveTab('discover')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'discover' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Globe className="w-4 h-4 inline mr-1.5" /> Discover
        </button>
      </div>

      {/* Search and Filters */}
      <GovCard className="mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </GovCard>

      {/* Pages Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-500 text-sm">Loading pages...</p>
        </div>
      ) : filteredPages.length === 0 ? (
        <GovEmptyState
          icon="🏛️"
          title={activeTab === 'following' ? 'Not following any pages' : 'No pages found'}
          description={activeTab === 'following' ? 'Discover organizations and causes to follow' : undefined}
          action={activeTab === 'following' ? { label: 'Discover pages', onClick: () => setActiveTab('discover') } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPages.map((page) => {
            const isFollowing = isFollowingPage(page.id)
            return (
              <GovCard key={page.id} className="overflow-hidden p-0 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedPage(page)}>
                {/* Cover */}
                <div className="h-20 bg-gradient-to-br from-blue-500 to-cyan-500 relative">
                  {page.coverImage && (
                    <img src={getMediaUrl(page.coverImage)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>

                {/* Profile */}
                <div className="relative px-4">
                  <div className="absolute -top-8 w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 border-3 border-white flex items-center justify-center overflow-hidden shadow-sm">
                    {page.profileImage ? (
                      <img src={getMediaUrl(page.profileImage)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Building2 className="w-6 h-6 text-white" />
                    )}
                  </div>
                  {page.isVerified && (
                    <div className="absolute -top-6 left-10 bg-blue-600 rounded-full p-0.5">
                      <Star className="w-3 h-3 text-white fill-white" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="pt-8 pb-4 px-4">
                  <h3 className="font-semibold text-gray-900 mb-0.5">{page.name}</h3>
                  <p className="text-gray-500 text-xs mb-2">{categories.find(c => c.value === page.category)?.label || page.category}</p>
                  {page.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{page.description}</p>
                  )}

                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                    <Users className="w-3.5 h-3.5" />
                    <span>{page.followerCount || 0} followers</span>
                  </div>

                  <GovButton
                    variant={isFollowing ? 'secondary' : 'primary'}
                    size="sm"
                    className="w-full"
                    onClick={(e: React.MouseEvent) => handleFollowClick(e, page.id)}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </GovButton>
                </div>
              </GovCard>
            )
          })}
        </div>
      )}

      {/* Create Page Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeModal}>
          <GovCard className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Create New Page</h2>
              <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                  placeholder="Enter page name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm"
                  rows={3}
                  placeholder="What is this page about?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                  placeholder="contact@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <GovButton
                onClick={createPage}
                disabled={!formData.name.trim() || creating}
                className="w-full"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create Page'}
              </GovButton>
            </div>
          </GovCard>
        </div>
      )}

      {/* Page Detail Modal */}
      {selectedPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closePageModal}>
          <GovCard className="w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden p-0" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {/* Cover */}
            <div className="h-32 bg-gradient-to-br from-blue-500 to-cyan-500 relative">
              {selectedPage.coverImage && (
                <img src={selectedPage.coverImage} alt="" className="w-full h-full object-cover" loading="lazy" />
              )}
              <button
                onClick={closePageModal}
                className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Profile */}
            <div className="relative px-6">
              <div className="absolute -top-10 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 border-4 border-white flex items-center justify-center overflow-hidden shadow-sm">
                {selectedPage.profileImage ? (
                  <img src={getMediaUrl(selectedPage.profileImage)} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <Building2 className="w-8 h-8 text-white" />
                )}
              </div>
            </div>

            {/* Content */}
            <div className="pt-12 pb-6 px-6 overflow-y-auto max-h-[calc(80vh-8rem)]">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-gray-900">{selectedPage.name}</h2>
                {selectedPage.isVerified && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">Verified</span>
                )}
              </div>
              <p className="text-gray-500 text-sm mb-4">{categories.find(c => c.value === selectedPage.category)?.label}</p>

              {selectedPage.description && (
                <p className="text-gray-700 text-sm mb-4">{selectedPage.description}</p>
              )}

              <div className="space-y-2 text-gray-600 text-sm mb-4">
                {selectedPage.contactEmail && (
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {selectedPage.contactEmail}</p>
                )}
                {selectedPage.contactPhone && (
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {selectedPage.contactPhone}</p>
                )}
                {selectedPage.website && (
                  <p className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <a href={selectedPage.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedPage.website}</a>
                  </p>
                )}
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {selectedPage.followerCount || 0} followers</span>
              </div>

              <GovButton
                variant={isFollowingPage(selectedPage.id) ? 'secondary' : 'primary'}
                className="w-full"
                onClick={() => {
                  isFollowingPage(selectedPage.id) ? unfollowPage(selectedPage.id) : followPage(selectedPage.id)
                }}
              >
                {isFollowingPage(selectedPage.id) ? 'Following' : 'Follow'}
              </GovButton>
            </div>
          </GovCard>
        </div>
      )}
    </GovLayout>
  )
}

export default React.memo(PagesPage)